/* [EZ] PDP Raw Body — scroll reveal.
   The scraped marketing bodies arrive with the source's animation intent but the source's own JS
   stripped, so this re-creates the reveal. Two builders express that intent differently, so there
   are two independent passes (a body only ever matches one of them):

   1) LEADPAGES bodies: elements carry data-lp-animate-section="<animate.css keyframe>" and start
      at visibility:hidden. On enter we make them visible and play the keyframe (keyframes live in
      the framework CSS). Reveal-once.

   2) WEBFLOW bodies: elements use .row (slide-up) / .slide_left|.slide-left (in from the left) /
      .slide_right|.slide-right (in from the right), hidden at the source via opacity:0. The
      scraper's static fallback (unhideReveals) force-shows them with `opacity:1 !important` so the
      page is never blank without JS — so to drive the animation here we set the hide/reveal state
      as INLINE !important styles, which outrank that stylesheet !important. Mirrors the source: a
      0.2 threshold to enter, a 20% rootMargin exit that re-arms it (so it replays on re-scroll),
      and the source's 200ms stagger for the .slide_right.01 / .slide_left.02 pair. Elements already
      in view on load are shown immediately (no first-paint flash); off-screen ones animate in.

   Both passes no-op when their markers/classes aren't present, and fall back to fully-visible when
   IntersectionObserver is unavailable. */
(function () {
  function initLeadpages() {
    var els = document.querySelectorAll('.ez-rawhtml [data-lp-animate-section]');
    if (!els.length) return;

    if (!('IntersectionObserver' in window)) {
      for (var i = 0; i < els.length; i++) els[i].style.visibility = 'visible';
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      for (var k = 0; k < entries.length; k++) {
        var e = entries[k];
        if (!e.isIntersecting) continue;
        var el = e.target;
        var name = el.getAttribute('data-lp-animate-section');
        el.style.visibility = 'visible';
        if (name) el.style.animation = name + ' var(--animate-duration, 0.8s) both';
        io.unobserve(el);
      }
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    for (var j = 0; j < els.length; j++) io.observe(els[j]);
  }

  function initWebflow() {
    // A body is EITHER leadpages (data-lp-animate-section) or webflow — never both. `.row` is
    // ambiguous: in webflow bodies it's a reveal target (source opacity:0 slide-up), but in
    // leadpages bodies it's just a Bootstrap-style layout grid row that must NOT be touched (the
    // leadpages pass reveals via visibility instead). So only claim `.row` when this is NOT a
    // leadpages body; `.slide_*` are unambiguous reveal classes and safe either way.
    var isLeadpages = !!document.querySelector('.ez-rawhtml [data-lp-animate-section]');
    var slideSel = '.ez-rawhtml .slide_left, .ez-rawhtml .slide-left, .ez-rawhtml .slide_right, .ez-rawhtml .slide-right';
    var els = document.querySelectorAll(isLeadpages ? slideSel : '.ez-rawhtml .row, ' + slideSel);
    if (!els.length) return;
    // No-JS / no-IO: the scraper's static fallback already shows everything — leave it visible.
    if (!('IntersectionObserver' in window)) return;

    var IMP = 'important';
    var TRANSITION = 'opacity 1000ms ease-in-out, transform 1000ms ease-in-out';
    var timers = new WeakMap();

    function isLeft(el) { return el.classList.contains('slide_left') || el.classList.contains('slide-left'); }
    function isRight(el) { return el.classList.contains('slide_right') || el.classList.contains('slide-right'); }
    function exitTransform(el) {
      if (isLeft(el)) return 'translate3d(-100px, 0px, 0px)';
      if (isRight(el)) return 'translate3d(100px, 0px, 0px)';
      return 'translate3d(0px, 100px, 0px)';      // .row → slide up
    }
    function enterDelay(el) {
      if (isRight(el) && el.classList.contains('01')) return 200;
      if (isLeft(el) && el.classList.contains('02')) return 200;
      return 0;
    }
    function clearTimer(el) { var t = timers.get(el); if (t) { clearTimeout(t); timers.delete(el); } }
    function hide(el) {
      el.style.setProperty('opacity', '0', IMP);
      el.style.setProperty('transform', exitTransform(el), IMP);
    }
    function show(el) {
      el.style.setProperty('opacity', '1', IMP);
      el.style.setProperty('transform', 'translate3d(0px, 0px, 0px)', IMP);
    }

    var enterObserver = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var e = entries[i];
        if (!e.isIntersecting) continue;
        var el = e.target;
        el.dataset.ezInview = '1';
        clearTimer(el);
        var delay = enterDelay(el);
        if (delay > 0) {
          timers.set(el, setTimeout((function (t) { return function () { if (t.dataset.ezInview === '1') show(t); }; })(el), delay));
        } else {
          show(el);
        }
      }
    }, { threshold: 0.2 });

    var exitObserver = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var e = entries[i];
        if (e.isIntersecting) continue;
        var el = e.target;
        el.dataset.ezInview = '0';
        clearTimer(el);
        hide(el);
      }
    }, { rootMargin: '20% 0px 20% 0px', threshold: 0 });

    var vh = window.innerHeight || document.documentElement.clientHeight;
    for (var n = 0; n < els.length; n++) {
      var el = els[n];
      el.style.setProperty('transition', TRANSITION, IMP);
      var r = el.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) { el.dataset.ezInview = '1'; show(el); }  // already visible — no flash
      else hide(el);
      enterObserver.observe(el);
      exitObserver.observe(el);
    }
  }


  /* 3) INSTALLATION GALLERY tabs (the HB90 bodies).
        Below 768px the three mount panels collapse to one visible panel plus a Wall/Ceiling/Pole
        tablist; at 768px and up the grid shows all three and the tablist is display:none. The markup
        arrived with the whole ARIA contract — role=tab, aria-controls, a roving tabindex, and
        .is-active on the Wall panel — but no script ever came with it, so the buttons were inert and
        mobile was stuck on Wall Mount for good. Same family as the two reveal passes above: the
        builder's intent survived the scrape, its JS did not.

        Panels are matched by aria-controls, which is the pairing the markup actually authored;
        data-installation-panel is the fallback. Arrow keys move between tabs like a real tablist. */
  function initInstallationTabs() {
    var lists = document.querySelectorAll('.ez-rawhtml [role="tablist"]');
    if (!lists.length) return;

    for (var i = 0; i < lists.length; i++) {
      bindTablist(lists[i]);
    }

    function bindTablist(list) {
      // Only ours — other scraped bodies have tablists of their own and must not be touched.
      var tabs = [].slice.call(list.querySelectorAll('[data-installation-tab]'));
      if (tabs.length < 2) return;

      function panelFor(tab) {
        var id = tab.getAttribute('aria-controls');
        var byId = id ? document.getElementById(id) : null;
        if (byId) return byId;
        var key = tab.getAttribute('data-installation-tab');
        return document.querySelector('.ez-rawhtml [data-installation-panel="' + key + '"]');
      }

      function select(tab, moveFocus) {
        for (var j = 0; j < tabs.length; j++) {
          var t = tabs[j];
          var on = t === tab;
          var panel = panelFor(t);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          if (panel) panel.classList.toggle('is-active', on);
        }
        if (moveFocus) tab.focus();
      }

      for (var k = 0; k < tabs.length; k++) {
        tabs[k].addEventListener('click', (function (t) {
          return function (ev) { ev.preventDefault(); select(t, false); };
        })(tabs[k]));
      }

      list.addEventListener('keydown', function (ev) {
        var dir = ev.key === 'ArrowRight' ? 1 : ev.key === 'ArrowLeft' ? -1 : 0;
        if (!dir) return;
        var at = tabs.indexOf(document.activeElement);
        if (at < 0) return;
        ev.preventDefault();
        select(tabs[(at + dir + tabs.length) % tabs.length], true);
      });

      // Normalise the start state from the markup, so exactly one panel is ever active.
      var initial = null;
      for (var n = 0; n < tabs.length; n++) {
        if (tabs[n].getAttribute('aria-selected') === 'true') { initial = tabs[n]; break; }
      }
      select(initial || tabs[0], false);
    }
  }

  function init() { initLeadpages(); initWebflow(); initInstallationTabs(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
